import React from 'react';
import { CompleteKundali, Language } from '../../types/kundali';
import { calculateQualityDistribution } from '../../utils/qualityArchetypeEngine';
import { Sparkles, Shield, RefreshCw, Compass, Info, CheckCircle2 } from 'lucide-react';

interface QualityDistributionCardProps {
  kundali: CompleteKundali;
  lang: Language;
}

export const QualityDistributionCard: React.FC<QualityDistributionCardProps> = ({
  kundali,
  lang
}) => {
  const isHi = lang === 'hi';
  const quality = calculateQualityDistribution(kundali, lang);
  const { creator, preserver, transformer, dominantZone, secondaryZone, isBalanced, rashiSpecificManifestation } = quality;

  return (
    <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 sm:p-7 shadow-xs space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 border-b border-astro-border/80 pb-5">
        <div className="space-y-1 max-w-2xl">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-bold uppercase tracking-wider bg-astro-accent/15 text-[#8C6428] px-2.5 py-0.5 rounded-full border border-astro-accent/30">
              {isHi ? 'त्रि-गुणात्मक स्वभाव विभाजन' : 'Creator / Preserver / Transformer Archetype'}
            </span>
            <span className="text-xs text-[#6B7280]">
              {isHi ? `लग्न: ${rashiSpecificManifestation.rashi}` : `Ascendant: ${rashiSpecificManifestation.rashi}`}
            </span>
          </div>
          <h3 className="text-2xl font-bold font-serif text-astro-text">
            {isHi ? rashiSpecificManifestation.archetypeTitleHi : rashiSpecificManifestation.archetypeTitle}
          </h3>
          <p className="text-xs sm:text-sm text-astro-secondary-text leading-relaxed">
            {isHi ? rashiSpecificManifestation.coreTraitHi : rashiSpecificManifestation.coreTrait}
          </p>
        </div>

        {/* Status Pill */}
        <div className="bg-[#FAF6ED] p-3 rounded-xl border border-astro-border shrink-0 text-center">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#6B7280]">
            {isHi ? 'प्रमुख ऊर्जा क्षेत्र' : 'Dominant Quality'}
          </div>
          <div className="text-lg font-black text-astro-primary mt-0.5">
            {dominantZone}
          </div>
          {secondaryZone !== 'NONE' && (
            <div className="text-[11px] text-astro-accent font-medium">
              {isHi ? `द्वितीयक: ${secondaryZone}` : `Secondary: ${secondaryZone}`}
            </div>
          )}
        </div>
      </div>

      {/* Visual Tri-Zone Quadrant Distribution Representation */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Creator Zone (Houses 1-4) */}
        <div
          className={`p-4 rounded-xl border transition-all ${
            dominantZone === 'CREATOR'
              ? 'bg-astro-primary/5 border-astro-primary shadow-xs'
              : 'bg-[#FAF6ED] border-astro-border'
          }`}
        >
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-1.5 font-bold text-sm text-astro-primary">
              <Sparkles className="w-4 h-4 text-astro-accent" />
              <span>{isHi ? 'सृजन क्षेत्र (भाव 1–4)' : 'Creator (H1–4)'}</span>
            </div>
            <span className="text-sm font-black text-astro-primary">{creator.percentage}%</span>
          </div>
          <div className="w-full bg-astro-card h-1.5 rounded-full overflow-hidden mb-3">
            <div className="bg-astro-primary h-full rounded-full" style={{ width: `${creator.percentage}%` }} />
          </div>
          <p className="text-xs text-astro-secondary-text mb-3 leading-relaxed">
            {isHi ? creator.descriptionHi : creator.description}
          </p>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] font-bold uppercase text-[#6B7280]">{isHi ? 'ग्रह:' : 'Planets:'}</span>
            {creator.planets.length > 0 ? (
              creator.planets.map(p => (
                <span key={p} className="text-[10px] font-semibold bg-white border border-astro-border px-2 py-0.5 rounded-md text-astro-text">
                  {p}
                </span>
              ))
            ) : (
              <span className="text-[10px] text-[#6B7280] italic">{isHi ? 'कोई नहीं' : 'None'}</span>
            )}
          </div>
        </div>

        {/* Preserver Zone (Houses 5-8) */}
        <div
          className={`p-4 rounded-xl border transition-all ${
            dominantZone === 'PRESERVER'
              ? 'bg-astro-primary/5 border-astro-primary shadow-xs'
              : 'bg-[#FAF6ED] border-astro-border'
          }`}
        >
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-1.5 font-bold text-sm text-astro-primary">
              <Shield className="w-4 h-4 text-astro-accent" />
              <span>{isHi ? 'संरक्षण क्षेत्र (भाव 5–8)' : 'Preserver (H5–8)'}</span>
            </div>
            <span className="text-sm font-black text-astro-primary">{preserver.percentage}%</span>
          </div>
          <div className="w-full bg-astro-card h-1.5 rounded-full overflow-hidden mb-3">
            <div className="bg-astro-primary h-full rounded-full" style={{ width: `${preserver.percentage}%` }} />
          </div>
          <p className="text-xs text-astro-secondary-text mb-3 leading-relaxed">
            {isHi ? preserver.descriptionHi : preserver.description}
          </p>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] font-bold uppercase text-[#6B7280]">{isHi ? 'ग्रह:' : 'Planets:'}</span>
            {preserver.planets.length > 0 ? (
              preserver.planets.map(p => (
                <span key={p} className="text-[10px] font-semibold bg-white border border-astro-border px-2 py-0.5 rounded-md text-astro-text">
                  {p}
                </span>
              ))
            ) : (
              <span className="text-[10px] text-[#6B7280] italic">{isHi ? 'कोई नहीं' : 'None'}</span>
            )}
          </div>
        </div>

        {/* Transformer Zone (Houses 9-12) */}
        <div
          className={`p-4 rounded-xl border transition-all ${
            dominantZone === 'TRANSFORMER'
              ? 'bg-astro-primary/5 border-astro-primary shadow-xs'
              : 'bg-[#FAF6ED] border-astro-border'
          }`}
        >
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-1.5 font-bold text-sm text-astro-primary">
              <RefreshCw className="w-4 h-4 text-astro-accent" />
              <span>{isHi ? 'रूपांतरण क्षेत्र (भाव 9–12)' : 'Transformer (H9–12)'}</span>
            </div>
            <span className="text-sm font-black text-astro-primary">{transformer.percentage}%</span>
          </div>
          <div className="w-full bg-astro-card h-1.5 rounded-full overflow-hidden mb-3">
            <div className="bg-astro-primary h-full rounded-full" style={{ width: `${transformer.percentage}%` }} />
          </div>
          <p className="text-xs text-astro-secondary-text mb-3 leading-relaxed">
            {isHi ? transformer.descriptionHi : transformer.description}
          </p>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] font-bold uppercase text-[#6B7280]">{isHi ? 'ग्रह:' : 'Planets:'}</span>
            {transformer.planets.length > 0 ? (
              transformer.planets.map(p => (
                <span key={p} className="text-[10px] font-semibold bg-white border border-astro-border px-2 py-0.5 rounded-md text-astro-text">
                  {p}
                </span>
              ))
            ) : (
              <span className="text-[10px] text-[#6B7280] italic">{isHi ? 'कोई नहीं' : 'None'}</span>
            )}
          </div>
        </div>
      </div>

      {/* Rashi-Specific Manifestation Insight Details */}
      <div className="bg-[#FAF6ED] rounded-xl p-4 sm:p-5 border border-astro-border space-y-3">
        <div className="text-xs font-bold uppercase tracking-wider text-astro-primary flex items-center gap-1.5">
          <Compass className="w-4 h-4 text-astro-accent" />
          {isHi ? `${rashiSpecificManifestation.rashi} लग्न व्यावहारिक कार्यशैली एवं दिशा:` : `Behavioral Blueprint for ${rashiSpecificManifestation.rashi} Ascendant:`}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <div className="bg-white p-3 rounded-lg border border-astro-border space-y-1">
            <span className="font-bold text-astro-primary block">{isHi ? 'कार्यशैली (Behavior):' : 'Operational Style:'}</span>
            <p className="text-astro-secondary-text">{isHi ? rashiSpecificManifestation.behavioralStyleHi : rashiSpecificManifestation.behavioralStyle}</p>
          </div>
          <div className="bg-white p-3 rounded-lg border border-astro-border space-y-1">
            <span className="font-bold text-astro-primary block">{isHi ? 'अनुकूल वातावरण (Environment):' : 'Optimal Environment:'}</span>
            <p className="text-astro-secondary-text">{isHi ? rashiSpecificManifestation.optimalEnvironmentHi : rashiSpecificManifestation.optimalEnvironment}</p>
          </div>
          <div className="bg-white p-3 rounded-lg border border-astro-border space-y-1">
            <span className="font-bold text-astro-primary block">{isHi ? 'सावधानी (Potential Pitfall):' : 'Potential Pitfall:'}</span>
            <p className="text-astro-secondary-text">{isHi ? rashiSpecificManifestation.potentialPitfallHi : rashiSpecificManifestation.potentialPitfall}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
